export interface PricingItem {
	quantity?: number;
	price: number; // in dollars
	id?: number | string;
	name?: string;
}

export interface PricingDiscountRule {
	testId: number;
	threshold: number;
	exclusive?: boolean;
	exclusiveIgnoreTestIds?: number[];
	discount?: {
		type?: string;
		amount?: number | string;
	};
}

export interface DiscountedPricingItem {
	id: number;
	name: string;
	price: number;
	quantities: number;
}

export interface PricingDiscountSummary {
	totalDiscounts: number;
	discountedTests: DiscountedPricingItem[];
}

export const resolveDiscountedUnitPrice = (
	item: PricingItem,
	items: PricingItem[] = [],
	discountRules: PricingDiscountRule[] = [],
): number | undefined => {
	const rule = discountRules.find((discountRule) => Number(discountRule.testId) === Number(item.id));
	const quantity = item.quantity ?? 1;
	const discountType = rule?.discount?.type;
	const discountAmount = Number(rule?.discount?.amount);

	if (!rule || quantity < rule.threshold) {
		return undefined;
	}

	if (rule.exclusive) {
		const ignoreIds = new Set(rule.exclusiveIgnoreTestIds ?? []);
		const otherSelectedItems = items.filter((pricingItem) => {
			return (
				(pricingItem.quantity ?? 0) > 0 &&
				Number(pricingItem.id) !== Number(item.id) &&
				!ignoreIds.has(Number(pricingItem.id))
			);
		});

		if (otherSelectedItems.length > 0) {
			return undefined;
		}
	}

	if (!discountType || Number.isNaN(discountAmount)) {
		return undefined;
	}

	const discountedPriceCents = PricingCalculator.resolveDiscountedPriceCents(
		Math.round(item.price * 100),
		discountType,
		discountAmount,
	);

	if (discountedPriceCents === null) {
		return undefined;
	}

	return discountedPriceCents / 100;
};

export class PricingCalculator {
	private items: PricingItem[];
	private discountRules: PricingDiscountRule[];

	constructor(items: PricingItem[], discountRules: PricingDiscountRule[] = []) {
		this.items = items;
		this.discountRules = discountRules;
	}

	static getDiscountSummary(items: PricingItem[], discountRules: PricingDiscountRule[] = []): PricingDiscountSummary {
		let totalDiscountCents = 0;
		const discountedTests: DiscountedPricingItem[] = [];

		for (const rule of discountRules) {
			const item = items.find((pricingItem) => Number(pricingItem.id) === Number(rule.testId));

			if (!item) {
				continue;
			}

			const quantity = item.quantity ?? 1;
			const discountedUnitPrice = resolveDiscountedUnitPrice(item, items, [rule]);

			if (discountedUnitPrice === undefined) {
				continue;
			}

			const originalPriceCents = Math.round(item.price * 100);
			const discountedPriceCents = Math.round(discountedUnitPrice * 100);

			const discountPerUnitCents = Math.max(originalPriceCents - discountedPriceCents, 0);

			if (discountPerUnitCents === 0) {
				continue;
			}

			totalDiscountCents += discountPerUnitCents * quantity;
			discountedTests.push({
				id: rule.testId,
				name: item.name || `Test ${rule.testId}`,
				price: discountedPriceCents / 100,
				quantities: quantity,
			});
		}

		return {
			totalDiscounts: totalDiscountCents / 100,
			discountedTests,
		};
	}

	static resolveDiscountedPriceCents(
		originalPriceCents: number,
		discountType: string,
		discountAmount: number,
	): number | null {
		if (discountType === 'PERCENTAGE') {
			return Math.round(originalPriceCents * (discountAmount / 100));
		}

		if (discountType === 'FIXED_PRICE') {
			return Math.round(discountAmount * 100);
		}

		return null;
	}

	// always work in cents internally
	private subtotalCents(): number {
		return this.items
			.filter((item) => {
				return 'quantity' in item && Number(item.quantity) > 0;
			})
			.reduce((sum, item) => {
				const quantity = item.quantity ?? 1;
				return sum + quantity * Math.round(item.price * 100);
			}, 0);
	}

	private discountCents(): number {
		const bulkDiscountCents = Math.round(
			PricingCalculator.getDiscountSummary(this.items, this.discountRules).totalDiscounts * 100,
		);
		return bulkDiscountCents;
	}

	private discountedSubtotalCents(): number {
		const subtotal = this.subtotalCents();
		const discount = this.discountCents();
		return Math.max(subtotal - discount, 0);
	}

	private gstCents(): number {
		// GST is 10% of the discounted subtotal
		return Math.round(this.discountedSubtotalCents() * 0.1);
	}

	private totalCents(): number {
		// apply GST (10%) after discount
		return this.discountedSubtotalCents() + this.gstCents();
	}

	private totalWithoutGstCents(): number {
		return this.discountedSubtotalCents();
	}

	private depositCents(percent = 0.3): number {
		return Math.round(this.totalCents() * percent);
	}

	private depositWithoutGstCents(percent = 0.3): number {
		return Math.round(this.totalWithoutGstCents() * percent);
	}

	private balanceCents(percent = 0.3): number {
		return this.totalCents() - this.depositCents(percent);
	}

	subtotal(): number {
		return this.subtotalCents() / 100;
	}

	discount(): number {
		return this.discountCents() / 100;
	}

	discountedSubtotal(): number {
		return this.discountedSubtotalCents() / 100;
	}

	gst(): number {
		return this.gstCents() / 100;
	}

	total(): number {
		return this.totalCents() / 100;
	}

	totalWithoutGST(): number {
		return this.totalWithoutGstCents() / 100;
	}

	deposit(percent = 0.3): number {
		return this.depositCents(percent) / 100;
	}

	depositWithoutGST(percent = 0.3): number {
		return this.depositWithoutGstCents(percent) / 100;
	}

	balance(percent = 0.3): number {
		return this.balanceCents(percent) / 100;
	}
}
